// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `trace-v1` — the active player's inputs, four times a second (M8-18).
//
// 10 §5: "**Incremental input-trace deltas from the active player**", and the paragraph under the
// table is the whole design: *"the server does not stream ball positions at 60 Hz — it streams
// your inputs, a few bytes per flip, and the spectator's own client re-simulates."*
//
// **Cadence changes latency and cannot change accuracy**, and that is the property everything here
// is built to keep. An action's timing lives in its *step number*, not in when the packet carrying
// it arrived, so sending at 4 Hz rather than 60 Hz does not blur a tap-passed skill shot — it just
// means the spectator learns about it up to 250 ms late. That is why this stream is allowed to be
// slow, lossy and bursty in ways a position stream never could be.
//
// **Nothing here is rounded.** A nudge impulse and a plunger power are inputs to a re-simulation
// that must reach the same number the player's machine did, and a value trimmed to three decimals
// on the wire is a value that diverges a few thousand steps later with nothing reporting why.
// Compactness is bought by dropping the field *names* — the events go as small integer tuples —
// and never by dropping precision.
//
// **A packet is self-describing and the events are the only incremental part.** Every delta
// carries the machine, the seed, the player count and the ball, roughly 150 bytes, so a client
// that joins mid-ball has everything it needs about the *game* immediately and is missing only
// history. Re-deriving that header from an earlier packet would make the first packet a client
// happened to receive special, which is precisely the packet a never-durable lane is entitled to
// lose (10 §5: "never durable").
//
// **Loss is handled twice, cheaply then expensively.** Every packet repeats the last
// `REDUNDANT_EVENTS` actions, so one lost packet — the common case — costs nothing at all and is
// repaired by the next one before anybody notices. A gap wider than that is repaired by asking:
// `trace-need-v1` carries a "from here, please" pointer and the player's client answers with a
// catch-up delta. That is the same shape as `game-notify-v1`'s "clients fetch on a gap", for the
// same reason — the alternative is a spectator silently watching a game that is not the one being
// played.
//
// **A trace belongs to a seat, and the turn passing starts a different one.** An online game is
// four machines each playing one player's game (M8-14), so seat two's trace is not a continuation
// of seat one's — different seed, different machine state, a different game entirely. A receiver
// that saw the seat change and appended would be re-simulating a chimera.
//
// **What this lane decides is what a spectator sees, and nothing else.** A broadcast payload has
// no authenticated author, so a seated player could publish a trace claiming to be somebody else's
// turn; the honest bound on that is the consequence, not the prevention. No score reaches a
// leaderboard this way — FR-034 admits a score only after the server re-simulates a submitted
// replay — so the worst available outcome is showing three people a ball that was not played.
//
// No clock in this module. Every function is handed the time it should act at, so the cadence can
// be tested rather than waited out.

import { MAX_TRACE_STEPS } from '@core/replay'
import { DEFAULT_INITIALS } from '@core/game'
import type { ReplayTrace, TraceAction, TraceCheckpoint, TraceEvent } from '@core/replay'

/** The event name, spelled exactly as 10 §5 spells it. */
export const TRACE_EVENT = 'trace-v1'

/** The companion event a spectator asks a gap back with. */
export const TRACE_NEED_EVENT = 'trace-need-v1'

/**
 * How often the active player publishes, milliseconds.
 *
 * 4 Hz. A ball produces a few actions a second, so this is usually one packet carrying two or
 * three tuples — and where it is not, batching is a saving rather than a delay, because the steps
 * inside the packet are exact whatever time it left. See the note at the top: cadence is latency.
 */
export const TRACE_INTERVAL_MS = 250

/**
 * How many already-sent actions every packet repeats.
 *
 * Sixteen, which is four packets' worth at a busy moment — so a spectator survives three
 * consecutive losses without asking for anything. The cost is about sixty bytes on a packet that
 * has a hundred and fifty bytes of header anyway, and the saving is the entire round trip of a
 * `trace-need-v1` and the catch-up that answers it.
 */
export const REDUNDANT_EVENTS = 16

/**
 * The most actions one packet may carry.
 *
 * A catch-up after a long gap is the case that needs a ceiling: a spectator who joins a ball two
 * minutes in is asking for several hundred actions, and one packet of unbounded size is how a
 * broadcast lane gets a client disconnected for exceeding a payload limit. Past this the answer
 * arrives over several ticks instead, which is invisible because the spectator is behind anyway.
 */
export const MAX_EVENTS_PER_DELTA = 192

/**
 * How many checkpoints ride along.
 *
 * Four, covering eight seconds. A checkpoint is what turns "the spectator's ball went somewhere
 * else" into "the runs diverged at step 34,000" — 10 §3's `divergent` outcome, and the thing
 * PH-088 exists to catch. Carrying the last few rather than only the newest means a receiver that
 * is a couple of seconds behind still has the one for the step it is about to pass.
 */
export const MARKS_CARRIED = 4

/** The most a spectator may ask, per second, so a confused one cannot flood the lane. */
export const NEED_EVERY_MS = 1_000

/** Longest string this reader will believe for a machine id or a version. */
const MAX_NAME = 64

/** A nudge impulse past this is a corrupt payload; the budget bounds a real one far below it. */
const MAX_IMPULSE = 16

/** What a delta says about the game it belongs to. Identical on every packet of one trace. */
export interface TraceHead {
  readonly gameId: string
  /** Whose trace this is (M8-14 seats). A change of seat is a different trace, not more of this. */
  readonly seat: number
  readonly machineId: string
  readonly machineVersion: string
  readonly coreVersion: string
  readonly seed: number
  readonly playerCount: number
  readonly ballsPerGame: number
  /**
   * Where initials entry opens, when it is not `DEFAULT_INITIALS` (M2-19a).
   *
   * **On the header, because a spectator re-simulates the whole game.** It is the same kind of
   * fact as the seed: not something the player did, and not inferable from what they did, so a
   * watcher who never received it would build a `Game` that starts on 'AAA' and diverge at the
   * first checkpoint after the table opens. Optional and absent-means-'AAA', so a sender that
   * predates it is still understood exactly.
   */
  readonly startingInitials?: string
}

/** One packet on the lane. */
export interface TraceDelta extends TraceHead {
  /** The ball being played, for a caption. Not used by the re-simulation, which derives it. */
  readonly ball: number
  /** The index in the whole trace of the first action carried. */
  readonly from: number
  readonly events: readonly TraceEvent[]
  /** The most recent checkpoints, newest last. */
  readonly marks: readonly TraceCheckpoint[]
  /** How many steps the sender's trace covers — how far a spectator may re-simulate. */
  readonly steps: number
}

/** "I am missing everything from here." */
export interface TraceNeed {
  readonly gameId: string
  readonly seat: number
  readonly from: number
}

// ---------------------------------------------------------------------------
// The wire shape
// ---------------------------------------------------------------------------
//
// Actions go as integer tuples rather than as objects, because `{"kind":"flipper","side":"left",
// "down":true}` is 44 bytes of field names to carry one bit, sixty times a ball. `[41200,0]` is
// nine. The codes are a closed vocabulary matching `TraceAction`'s, and an unknown one is dropped
// rather than guessed — a later core version adding an action is exactly the case where guessing
// produces a re-simulation that runs happily to the wrong answer.

const FLIPPER_LEFT_DOWN = 0
const FLIPPER_LEFT_UP = 1
const FLIPPER_RIGHT_DOWN = 2
const FLIPPER_RIGHT_UP = 3
const LAUNCH = 4
const NUDGE = 5
/**
 * The player declined the high-score table (M2-19a).
 *
 * **Six, and never one of the five above.** The comment at the top of this block already says why
 * the codes are written out: an unknown one is dropped rather than guessed, because a later core
 * adding an action is exactly the case where guessing produces a re-simulation that runs happily
 * to the wrong answer. A spectator on an older build now drops a skip and diverges *visibly* at
 * the checkpoint, which is the outcome that block was designed for.
 */
const SKIP = 6

/**
 * A coin paid into the coin door (06 §2.4, M7-35).
 *
 * Seven, on the same argument as six: written out, never derived from a union's order, and
 * dropped rather than guessed by a build that predates it. A spectator on an older build drops
 * the credit and diverges *visibly* at the next checkpoint, which is the whole design of this
 * block — a credit is inside `Game.fingerprint`, so a build that silently ignored one would
 * re-simulate happily to a hash nobody else computes.
 */
const CREDIT = 7

/** One action as `[step, code, …]`. */
export function encodeEvent(event: TraceEvent): readonly number[] {
  const { step, action } = event
  switch (action.kind) {
    case 'flipper':
      return [
        step,
        action.side === 'left'
          ? action.down
            ? FLIPPER_LEFT_DOWN
            : FLIPPER_LEFT_UP
          : action.down
            ? FLIPPER_RIGHT_DOWN
            : FLIPPER_RIGHT_UP,
      ]
    case 'launch':
      return [step, LAUNCH, action.power]
    case 'nudge':
      return [step, NUDGE, action.ix, action.iy]
    case 'skip':
      return [step, SKIP]
    case 'credit':
      return [step, CREDIT]
  }
}

/** …and back, or nothing for any shape this build does not recognise. */
export function decodeEvent(raw: unknown): TraceEvent | undefined {
  if (!Array.isArray(raw) || raw.length < 2) return undefined
  const step = raw[0]
  const code = raw[1]
  if (typeof step !== 'number' || !Number.isInteger(step)) return undefined
  if (step < 0 || step > MAX_TRACE_STEPS) return undefined
  if (typeof code !== 'number') return undefined

  const action = decodeAction(code, raw)
  return action === undefined ? undefined : { step, action }
}

function decodeAction(code: number, raw: readonly unknown[]): TraceAction | undefined {
  switch (code) {
    case FLIPPER_LEFT_DOWN:
      return { kind: 'flipper', side: 'left', down: true }
    case FLIPPER_LEFT_UP:
      return { kind: 'flipper', side: 'left', down: false }
    case FLIPPER_RIGHT_DOWN:
      return { kind: 'flipper', side: 'right', down: true }
    case FLIPPER_RIGHT_UP:
      return { kind: 'flipper', side: 'right', down: false }
    case LAUNCH: {
      const power = raw[2]
      // A plunger is pulled between nothing and all the way back. Out of range is a corrupt
      // packet rather than a strong player, and clamping it would re-simulate a different launch.
      if (typeof power !== 'number' || !Number.isFinite(power)) return undefined
      if (power < 0 || power > 1) return undefined
      return { kind: 'launch', power }
    }
    case NUDGE: {
      const ix = raw[2]
      const iy = raw[3]
      if (typeof ix !== 'number' || !Number.isFinite(ix) || Math.abs(ix) > MAX_IMPULSE) {
        return undefined
      }
      if (typeof iy !== 'number' || !Number.isFinite(iy) || Math.abs(iy) > MAX_IMPULSE) {
        return undefined
      }
      return { kind: 'nudge', ix, iy }
    }
    case SKIP:
      // No arguments to validate: a refusal is the whole of the message.
      return { kind: 'skip' }
    case CREDIT:
      // Nor here: a coin is the whole of the message.
      return { kind: 'credit' }
    default:
      return undefined
  }
}

/** A delta, as the object that goes on the lane. */
export function writeDelta(delta: TraceDelta): Record<string, unknown> {
  return {
    gameId: delta.gameId,
    seat: delta.seat,
    machineId: delta.machineId,
    machineVersion: delta.machineVersion,
    coreVersion: delta.coreVersion,
    seed: delta.seed,
    playerCount: delta.playerCount,
    ballsPerGame: delta.ballsPerGame,
    // Omitted at its default, so the common packet does not carry three bytes saying 'AAA'.
    ...(delta.startingInitials === undefined ? {} : { startingInitials: delta.startingInitials }),
    ball: delta.ball,
    from: delta.from,
    steps: delta.steps,
    events: delta.events.map(encodeEvent),
    marks: delta.marks.map((mark) => [mark.step, mark.hash]),
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu

/**
 * Read one delta, or nothing.
 *
 * Nothing for every malformed shape, and a caller treats that exactly as it treats a packet that
 * never arrived — the redundancy window repairs it, or the need pointer does.
 *
 * **An unreadable action refuses the whole packet** rather than being skipped. A trace with a hole
 * in it is not a shorter trace, it is a different game: skipping the "left flipper up" leaves a
 * bat held down for the rest of the ball.
 */
export function readDelta(raw: unknown): TraceDelta | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const payload = raw as Record<string, unknown>

  const gameId = payload['gameId']
  if (typeof gameId !== 'string' || !UUID.test(gameId)) return undefined

  const seat = counted(payload['seat'], 1, 4)
  if (seat === undefined) return undefined

  const machineId = named(payload['machineId'])
  const machineVersion = named(payload['machineVersion'])
  const coreVersion = named(payload['coreVersion'])
  if (machineId === undefined || machineVersion === undefined || coreVersion === undefined) {
    return undefined
  }

  const seed = counted(payload['seed'], 0, Number.MAX_SAFE_INTEGER)
  const playerCount = counted(payload['playerCount'], 1, 4)
  const ballsPerGame = counted(payload['ballsPerGame'], 1, 10)
  const ball = counted(payload['ball'], 0, 10)
  const from = counted(payload['from'], 0, MAX_TRACE_STEPS)
  const steps = counted(payload['steps'], 0, MAX_TRACE_STEPS)
  if (
    seed === undefined ||
    playerCount === undefined ||
    ballsPerGame === undefined ||
    ball === undefined ||
    from === undefined ||
    steps === undefined
  ) {
    return undefined
  }

  const rawEvents = payload['events']
  if (!Array.isArray(rawEvents) || rawEvents.length > MAX_EVENTS_PER_DELTA) return undefined
  const events: TraceEvent[] = []
  for (const item of rawEvents) {
    const event = decodeEvent(item)
    if (event === undefined) return undefined
    events.push(event)
  }

  const rawMarks = payload['marks']
  const marks: TraceCheckpoint[] = []
  if (rawMarks !== undefined) {
    if (!Array.isArray(rawMarks) || rawMarks.length > MARKS_CARRIED) return undefined
    for (const item of rawMarks) {
      const mark = decodeMark(item)
      if (mark === undefined) return undefined
      marks.push(mark)
    }
  }

  // Absent is the ordinary case and means 'AAA'. A value of the wrong shape is dropped rather
  // than carried: `Game` would resolve it to 'AAA' anyway, and a header that disagreed with what
  // the sender used is worse than one that says nothing.
  const startingInitials = initials(payload['startingInitials'])

  return {
    gameId,
    seat,
    machineId,
    machineVersion,
    coreVersion,
    seed,
    playerCount,
    ballsPerGame,
    ...(startingInitials === undefined ? {} : { startingInitials }),
    ball,
    from,
    steps,
    events,
    marks,
  }
}

/** Three characters, or nothing. Never a longer string: this is a fixed-width field. */
function initials(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length === 3 ? raw : undefined
}

function decodeMark(raw: unknown): TraceCheckpoint | undefined {
  if (!Array.isArray(raw) || raw.length < 2) return undefined
  const step = counted(raw[0], 0, MAX_TRACE_STEPS)
  const hash = raw[1]
  if (step === undefined) return undefined
  if (typeof hash !== 'number' || !Number.isInteger(hash)) return undefined
  return { step, hash }
}

/** A need pointer, as the object that goes on the lane. */
export function writeNeed(need: TraceNeed): Record<string, unknown> {
  return { gameId: need.gameId, seat: need.seat, from: need.from }
}

/** …and back. */
export function readNeed(raw: unknown): TraceNeed | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const payload = raw as Record<string, unknown>
  const gameId = payload['gameId']
  if (typeof gameId !== 'string' || !UUID.test(gameId)) return undefined
  const seat = counted(payload['seat'], 1, 4)
  const from = counted(payload['from'], 0, MAX_TRACE_STEPS)
  if (seat === undefined || from === undefined) return undefined
  return { gameId, seat, from }
}

function counted(raw: unknown, low: number, high: number): number | undefined {
  if (typeof raw !== 'number' || !Number.isInteger(raw)) return undefined
  return raw < low || raw > high ? undefined : raw
}

function named(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw === '' || raw.length > MAX_NAME) return undefined
  return raw
}

/** Do two headers describe the same trace? A seat change is a different one — see the top. */
export function sameTrace(a: TraceHead, b: TraceHead): boolean {
  return (
    a.gameId === b.gameId &&
    a.seat === b.seat &&
    a.seed === b.seed &&
    a.machineId === b.machineId &&
    a.machineVersion === b.machineVersion &&
    a.coreVersion === b.coreVersion &&
    a.playerCount === b.playerCount &&
    a.ballsPerGame === b.ballsPerGame &&
    // Compared through the default both ends resolve it to, so a sender that upgrades mid-game and
    // starts naming 'AAA' explicitly does not read as a different trace.
    (a.startingInitials ?? DEFAULT_INITIALS) === (b.startingInitials ?? DEFAULT_INITIALS)
  )
}

// ---------------------------------------------------------------------------
// The sending half
// ---------------------------------------------------------------------------

/** What the sender reads off the recorder each tick. */
export interface TraceSample {
  readonly events: readonly TraceEvent[]
  readonly marks: readonly TraceCheckpoint[]
  readonly steps: number
  readonly ball: number
}

/**
 * A machine's own trace, offered for streaming: the sample, plus what it is a trace of.
 *
 * **The seat and the game id are deliberately absent.** A machine knows what it is simulating and
 * has no idea whether anybody is watching or which chair the player is sitting in — those are the
 * online game's business, and a runtime that had to be told them before it could describe its own
 * trace would be a runtime that could not be built without a backend (FR-030).
 */
export interface LocalTrace extends TraceSample {
  readonly machineId: string
  readonly machineVersion: string
  readonly coreVersion: string
  readonly seed: number
  readonly playerCount: number
  readonly ballsPerGame: number
}

/**
 * Decides what to put on the lane and when.
 *
 * Pure and clockless: handed a sample and a time, it answers with a packet or with nothing. The
 * channel plumbing that calls it lives in `src/client/account/traceLink.ts`, which is where the
 * Supabase dependency belongs and where this must never acquire one — the anonymous funnel
 * (FR-030) walks modules, and a `src/net` module that imported the SDK would drag it onto the
 * front door the first time anything typed against it.
 */
export class TraceSender {
  private readonly head: TraceHead
  /** How many actions have been published at least once. */
  private sent = 0
  private lastAt = -Infinity
  private lastSteps = -1

  constructor(head: TraceHead) {
    this.head = head
  }

  /** What this sender is publishing, so a caller can tell whether it is still the right one. */
  get trace(): TraceHead {
    return this.head
  }

  /** How many actions this sender believes a spectator has been told about. */
  get published(): number {
    return this.sent
  }

  /**
   * The packet to send now, or nothing.
   *
   * Nothing when the interval has not elapsed, and nothing when it has but the trace has not moved
   * — a machine sitting in attract or waiting between balls has nothing to say, and saying it four
   * times a second for the minute a player spends deciding is a minute of noise on a shared lane.
   */
  sample(sample: TraceSample, nowMs: number): TraceDelta | undefined {
    if (nowMs - this.lastAt < TRACE_INTERVAL_MS) return undefined
    if (sample.events.length === this.sent && sample.steps === this.lastSteps) return undefined

    this.lastAt = nowMs
    this.lastSteps = sample.steps
    // **The window starts behind what has already been sent, not at it.** Repeating the last
    // sixteen actions is what makes a single lost packet cost nothing; see `REDUNDANT_EVENTS`.
    const from = Math.max(0, this.sent - REDUNDANT_EVENTS)
    const delta = this.cut(sample, from)
    this.sent = sample.events.length
    return delta
  }

  /**
   * The catch-up a `trace-need-v1` asked for.
   *
   * Bounded to one packet's worth: a spectator that asked from zero two minutes into a ball is
   * answered with the first `MAX_EVENTS_PER_DELTA` actions and asks again for the rest. Sending it
   * all at once is how a client gets dropped for an oversize payload, and the spectator is behind
   * either way.
   *
   * Does not touch the cadence — a catch-up is somebody else's problem being answered, and letting
   * it reset `lastAt` would stall the live stream every time a spectator joined.
   */
  catchUp(need: TraceNeed, sample: TraceSample): TraceDelta | undefined {
    if (need.gameId !== this.head.gameId || need.seat !== this.head.seat) return undefined
    if (need.from > sample.events.length) return undefined
    return this.cut(sample, need.from)
  }

  private cut(sample: TraceSample, from: number): TraceDelta {
    const events = sample.events.slice(from, from + MAX_EVENTS_PER_DELTA)
    return {
      ...this.head,
      ball: sample.ball,
      from,
      events,
      marks: sample.marks.slice(-MARKS_CARRIED),
      steps: sample.steps,
    }
  }
}

// ---------------------------------------------------------------------------
// The receiving half
// ---------------------------------------------------------------------------

/** What accepting a packet did. */
export type TraceUptake =
  /** The trace grew, or a checkpoint arrived. */
  | 'applied'
  /** Nothing new in it. The common outcome, because every packet repeats its predecessor's tail. */
  | 'known'
  /** It starts past the end of what is held. Ask, and see `need`. */
  | 'gap'
  /** A different trace entirely, or a different game. */
  | 'ignored'

/**
 * Puts the packets back together into a trace that can be re-simulated (M8-19).
 *
 * **Contiguous or nothing.** The assembled trace is always a prefix of the player's — never one
 * with a hole patched over — because a re-simulation is only correct if every action before the
 * step it has reached is present. A packet beyond the end is remembered as a *gap* and its events
 * are discarded rather than stored for later: holding them would mean carrying a second, sparse
 * representation and reassembling it, to save one round trip on a lane where the round trip is a
 * quarter of a second.
 */
export class TraceAssembler {
  private head: TraceHead | undefined
  private readonly events: TraceEvent[] = []
  private readonly marks: TraceCheckpoint[] = []
  private steps = 0
  private ball = 0
  private missing = false

  /** The header of the trace being assembled, or nothing before the first packet. */
  get current(): TraceHead | undefined {
    return this.head
  }

  /** How many actions are held, contiguously from the start. */
  get length(): number {
    return this.events.length
  }

  /** How far the player's own machine has run. What a spectator may re-simulate up to. */
  get liveSteps(): number {
    return this.steps
  }

  /** The ball the player is on, for a caption. */
  get liveBall(): number {
    return this.ball
  }

  /** Is something missing that only a catch-up can supply? */
  get gapped(): boolean {
    return this.missing
  }

  /** The pointer to ask with, or nothing when nothing is missing. */
  get need(): TraceNeed | undefined {
    if (!this.missing || this.head === undefined) return undefined
    return { gameId: this.head.gameId, seat: this.head.seat, from: this.events.length }
  }

  accept(delta: TraceDelta): TraceUptake {
    if (this.head === undefined || !sameTrace(this.head, delta)) {
      // **A different seat is a different game, so this starts over rather than appending.** In an
      // online game each player's machine plays their own game with their own seed (M8-14); seat
      // two's actions applied to seat one's trace would re-simulate a machine nobody played.
      if (delta.from > 0) {
        // Joined mid-ball. Nothing to append to yet, and the need pointer is what fixes it.
        this.reset(delta)
        this.steps = delta.steps
        this.missing = true
        return 'gap'
      }
      this.reset(delta)
    }

    if (delta.from > this.events.length) {
      this.steps = Math.max(this.steps, delta.steps)
      this.ball = delta.ball
      this.missing = true
      return 'gap'
    }

    let grew = false
    for (let i = this.events.length - delta.from; i < delta.events.length; i++) {
      const event = delta.events[i]
      if (event === undefined) continue
      this.events.push(event)
      grew = true
    }
    for (const mark of delta.marks) {
      if (this.marks.some((held) => held.step === mark.step)) continue
      this.marks.push(mark)
      grew = true
    }
    // Newest wins for the two live numbers, and an out-of-order packet cannot wind them back: the
    // lane has no ordering guarantee and a spectator that un-learned a step would stutter.
    if (delta.steps > this.steps) {
      this.steps = delta.steps
      grew = true
    }
    if (delta.ball > this.ball) this.ball = delta.ball
    this.missing = false
    return grew ? 'applied' : 'known'
  }

  /**
   * The trace so far, ready for `replayTrace` or for a live ghost (M8-19).
   *
   * `claimedScore` is zero and stays zero: a spectator's copy is never submitted anywhere, and a
   * number here would be a claim this client is in no position to make. The score a spectator sees
   * comes out of its own re-simulation, which is the entire point.
   */
  trace(): ReplayTrace | undefined {
    const head = this.head
    if (head === undefined) return undefined
    return {
      machineId: head.machineId,
      machineVersion: head.machineVersion,
      coreVersion: head.coreVersion,
      seed: head.seed,
      playerCount: head.playerCount,
      ballsPerGame: head.ballsPerGame,
      ...(head.startingInitials === undefined ? {} : { startingInitials: head.startingInitials }),
      events: [...this.events],
      checkpoints: [...this.marks].sort((a, b) => a.step - b.step),
      steps: this.steps,
      claimedScore: 0,
    }
  }

  private reset(delta: TraceDelta): void {
    this.head = {
      gameId: delta.gameId,
      seat: delta.seat,
      machineId: delta.machineId,
      machineVersion: delta.machineVersion,
      coreVersion: delta.coreVersion,
      seed: delta.seed,
      playerCount: delta.playerCount,
      ballsPerGame: delta.ballsPerGame,
      ...(delta.startingInitials === undefined ? {} : { startingInitials: delta.startingInitials }),
    }
    this.events.length = 0
    this.marks.length = 0
    this.steps = 0
    this.ball = delta.ball
    this.missing = false
  }
}
